import React from "react";
import LegalPage from "../components/LegalPage";

const SECTIONS = [
  {
    id: "introduction",
    heading: "1. Introduction",
    body: [
      "1.1  These Terms and Conditions govern access to and use of the Medily website, student portal, digital resources, AI interview tools, online courses, mock MMI preparation, live sessions and any other products or services supplied by Medily.",
      "1.2  By accessing the website, creating an account, purchasing any Service, using the student portal, attending any session or using Medily content, you agree to be bound by these Terms.",
      "1.3  If you do not agree with these Terms, you must not use the website, create an account, purchase Services or access Medily content.",
      "1.4  These Terms should be read together with the Privacy Policy, Cookie Policy, Refund and Cancellation Policy, AI Disclaimer, Safeguarding Policy and any product-specific terms shown at checkout.",
    ],
  },
  {
    id: "about-medily",
    heading: "2. About Medily",
    body: [
      "2.1  Medily provides educational support and interview preparation services for prospective medical school applicants.",
      "2.2  Services may include digital resources, university-specific preparation, interview question banks, AI-powered interview practice, written feedback, live teaching, mock MMI sessions and related educational services.",
      "2.3  Medily is independent. Unless expressly stated in writing, Medily is not affiliated with, endorsed by or formally connected to any medical school, university, UCAS, NHS body, admissions authority or regulator.",
      "2.4  Medily does not make admissions decisions and does not act for any university or admissions panel.",
    ],
  },
  {
    id: "definitions",
    heading: "3. Definitions",
    body: [
      "3.1  “Medily”, “we”, “us” and “our” means Medily Ltd.",
      "3.2  “User”, “you” and “your” means any person who accesses the website, creates an account, purchases Services or uses the platform.",
      "3.3  “Services” means all products and services provided by Medily, including digital resources, subscriptions, courses, AI tools, live sessions, mock interviews, feedback and student portal access.",
      "3.4  “Digital Content” means content supplied in digital form, including videos, PDFs, written resources, question banks, templates, AI-generated feedback, transcripts, recordings and online course materials.",
      "3.5  “AI Interviewer” means any artificial intelligence tool, chatbot, voice-based tool, video-based tool or automated feedback system provided by Medily.",
      "3.6  “Subscription” means recurring paid access to any Medily product or service.",
      "3.7  “Live Session” means any live online or in-person teaching, workshop, mock interview, webinar or preparation session.",
      "3.8  “Student” means the person using the Services for medical school interview preparation, whether they made the purchase personally.",
    ],
  },
  {
    id: "eligibility-and-under-18-users",
    heading: "4. Eligibility and Under-18 Users",
    body: [
      "4.1  You must be at least 13 years old to use Medily.",
      "4.2  If you are under 18, you confirm that you have permission from a parent or legal guardian to use the Services and, where relevant, to make purchases.",
      "4.3  A parent or legal guardian who purchases Services for a student is responsible for ensuring the student complies with these Terms.",
      "4.4  Medily may request evidence of parental or guardian consent where reasonably necessary, including for certain live sessions, in-person sessions, recordings or welfare-related matters.",
      "4.5  Medily may restrict, suspend or refuse access where we reasonably believe use by a minor creates a safeguarding, welfare, legal or data protection risk.",
    ],
  },
  {
    id: "account-registration-and-security",
    heading: "5. Account Registration and Security",
    body: [
      "5.1  Certain Services require a Medily account.",
      "5.2  You agree to provide accurate, complete and current information when registering or purchasing.",
      "5.3  You must keep login details confidential and must not share your account with any other person.",
      "5.4  You are responsible for all activity under your account unless caused by Medily’s own security failure.",
      "5.5  You must notify Medily immediately if you suspect unauthorised access, account sharing, compromised credentials or fraudulent use.",
      "5.6  Medily may suspend or terminate accounts where we reasonably suspect account sharing, fraud, misuse, unlawful conduct or breach of these Terms.",
    ],
  },
  {
    id: "products-and-services",
    heading: "6. Products and Services",
    body: [
      "6.1  Medily may offer Resources, Course access, Bundle access, AI Interviewer access, mock MMI sessions, workshops, one-off digital products, subscription-based access and other educational services.",
      "6.2  The exact content, price, access period, delivery method, technical requirements and limitations of each product will be described on the relevant sales page or at checkout.",
      "6.3  Medily may improve, update, replace, remove or modify content and platform features from time to time, provided this does not materially reduce an active paid Service without reasonable justification.",
      "6.4  Some Services may be subject to capacity limits, booking availability, interviewer availability or technical restrictions.",
    ],
  },
  {
    id: "ordering-pricing-and-payment",
    heading: "7. Ordering, Pricing and Payment",
    body: [
      "7.1  Prices are displayed before purchase and, unless stated otherwise, are shown in pounds sterling.",
      "7.2  Medily may change prices at any time. Price changes will not affect completed one-off purchases, but may apply to future subscription periods where notice is provided as required.",
      "7.3  Payments may be processed through third-party payment providers such as Stripe. You may be required to comply with the payment provider’s own terms.",
      "7.4  Medily does not store full card details. Payment information is processed by the payment provider.",
      "7.5  Access may be delayed, suspended or cancelled if payment fails, is reversed, is charged back, is suspected to be fraudulent, or remains outstanding.",
      "7.6  You must not misuse chargeback processes. Where a chargeback is raised improperly after Services have been supplied, Medily may suspend access and provide evidence of purchase, access and delivery to the payment provider.",
      "7.7  Users must ensure payment details remain valid. Subscription access may be suspended until outstanding payments are received.",
    ],
  },
  {
    id: "subscriptions-and-auto-renewal",
    heading: "8. Subscriptions and Auto-Renewal",
    body: [
      "8.1  Where you purchase a Subscription, it will automatically renew at the end of each billing period unless cancelled before renewal.",
      "8.2  The Subscription price, billing period, renewal date and cancellation method will be shown at checkout or in your account area.",
      "8.3  You authorise Medily and its payment provider to take recurring payments from your selected payment method until cancellation.",
      "8.4  You may cancel a Subscription through your account area or by contacting info@medily.co.",
      "8.5  Cancellation stops future billing. Unless required by law or stated in the Refund and Cancellation Policy, cancellation does not automatically entitle you to a refund for a billing period already started.",
      "8.6  You may cancel subscriptions through your account or by emailing info@medily.co. Cancellation prevents future renewals only and does not entitle you to a refund for the current billing period unless required by law.",
      "8.7  Medily will aim to make cancellation reasonably clear and accessible. You should retain confirmation of cancellation.",
    ],
  },
  {
    id: "digital-content-access",
    heading: "9. Digital Content Access",
    body: [
      "9.1  Digital Content may be made available immediately after purchase.",
      "9.2  Where you request or agree to immediate access to Digital Content during the statutory cancellation period, you acknowledge that cancellation rights may be affected once supply begins, subject to applicable consumer law.",
      "9.3  Digital Content is licensed for personal educational use only and is not sold to you.",
      "9.4  You must not copy, reproduce, distribute, resell, publish, upload, record, screenshot, scrape or otherwise exploit Digital Content without Medily’s written permission.",
      "9.5  Access periods may be limited and will be stated at purchase where applicable.",
    ],
  },
  {
    id: "courses-live-sessions-and-mock-interviews",
    heading: "10. Courses, Live Sessions and Mock Interviews",
    body: [
      "10.1  Live Sessions may be delivered online or in person.",
      "10.2  You are responsible for attending on time and ensuring you have suitable internet access, hardware, software, camera, microphone and a quiet environment where required.",
      "10.3  If you are late, miss a session or fail to attend, Medily is not obliged to provide a refund or replacement session unless required by law or agreed in writing.",
      "10.4  Medily may reschedule a Live Session due to illness, staff availability, technical failure, venue issues, low attendance, safety concerns or circumstances outside our control.",
      "10.5  If Medily cancels a paid Live Session and cannot provide a suitable alternative, Medily may offer an appropriate refund for the affected session.",
      "10.6  Mock interview feedback reflects the view of the interviewer or system at the time and is for preparation only. It is not an official admissions assessment.",
      "10.7  Mock MMI bookings are subject to the booking policy shown at checkout. Late arrival, insufficient notice or non-attendance will result in loss of the session without refund unless Medily agrees otherwise.",
    ],
  },
  {
    id: "ai-interviewer-and-ai-feedback",
    heading: "11. AI Interviewer and AI Feedback",
    body: [
      "11.1  The AI Interviewer (‘Maya’) is designed to support practice, reflection and preparation.",
      "11.2  AI-generated questions, prompts, scores, transcripts, summaries and feedback are educational tools only.",
      "11.3  AI output may be inaccurate, incomplete, inconsistent, outdated, misunderstood, biased, unsuitable for your circumstances or different from the approach of a university admissions panel.",
      "11.4  You must not rely solely on AI feedback. You should use your own judgment, human feedback and official university guidance.",
      "11.5  Medily does not guarantee that use of the AI Interviewer will improve performance, secure an interview, obtain an offer or result in admission to medical school.",
      "11.6  You must not submit sensitive personal data, medical information, third-party confidential information or unlawful content into the AI Interviewer unless specifically requested and clearly necessary for the Service.",
      "11.7  Use of the AI Interviewer may involve processing of written responses, audio, video, transcripts, scores or feedback reports as described in the Privacy Policy.",
      "11.8  Where the AI Interviewer generates university-specific interview questions or scenarios, these are intended solely as realistic practice materials based on Medily's educational methodologies. Medily does not guarantee that the questions generated accurately reflect, replicate or predict the interview questions, themes, format or assessment criteria used by any specific medical school, nor that any generated question will appear in an actual interview.",
    ],
  },
  {
    id: "educational-disclaimer",
    heading: "12. Educational Disclaimer",
    body: [
      "12.1  Medily provides educational preparation only.",
      "12.2  Medily does not guarantee medical school admission, interview invitations, university offers, score improvements, success in any application process or that any question will appear in a real interview.",
      "12.3  Testimonials, examples, sample answers, success stories and performance statements are illustrative only and do not guarantee future outcomes.",
      "12.4  Admissions criteria, interview formats and university procedures may change. You are responsible for checking official university and UCAS guidance.",
    ],
  },
  {
    id: "user-conduct-and-acceptable-use",
    heading: "13. User Conduct and Acceptable Use",
    body: [
      "13.1  You must use Medily lawfully, respectfully and only for legitimate personal educational preparation.",
      "13.2  You must not abuse, harass, threaten, discriminate against or intimidate any staff member, contractor, interviewer, student or user.",
      "13.3  You must not use offensive, hateful, sexually explicit, violent, defamatory or unlawful language or content.",
      "13.4  You must not interfere with platform security, upload malware, scrape the website, bypass access controls or attempt unauthorised access.",
      "13.5  You must not impersonate another person, submit misleading information or use Medily for fraudulent purposes.",
      "13.6  Medily may remove content, suspend access or terminate accounts where conduct breaches this clause or creates risk to users, staff, the platform or the business.",
    ],
  },
  {
    id: "intellectual-property",
    heading: "14. Intellectual Property",
    body: [
      "14.1  All intellectual property rights in Medily content, resources, branding, question banks, prompts, videos, documents, designs, feedback systems, software and teaching materials belong to Medily or its licensors.",
      "14.2  You are granted a limited, personal, non-exclusive, non-transferable, revocable licence to access and use Medily content for your own educational preparation only.",
      "14.3  You do not acquire ownership of Medily content by purchasing or accessing Services.",
      "14.4  You must not use Medily’s name, logo, branding, content or materials in any way that suggests endorsement, partnership or affiliation without written permission.",
      "14.5  Medily reserves all rights not expressly granted in these Terms.",
    ],
  },
  {
    id: "prohibited-sharing-and-anti-piracy",
    heading: "15. Prohibited Sharing and Anti-Piracy",
    body: [
      "15.1  You must not share Medily resources with friends, classmates, group chats, schools, tutors, online communities or other third parties.",
      "15.2  You must not upload Medily materials to websites, forums, file-sharing platforms, cloud folders, social media, messaging groups or AI tools.",
      "15.3  You must not reproduce Medily content in your own tutoring, education, coaching, admissions, AI or interview-preparation service.",
      "15.4  You must not use Medily content to train AI models, build competing products or create derivative commercial materials.",
      "15.5  Medily may investigate suspected misuse and may suspend accounts, terminate access, refuse refunds, issue takedown notices and pursue legal remedies where appropriate.",
    ],
  },
  {
    id: "user-content-recordings-and-transcripts",
    heading: "16. User Content, Recordings and Transcripts",
    body: [
      "16.1  You may submit answers, recordings, transcripts, written responses, messages and other content through the Services.",
      "16.2  You retain ownership of your own original User Content.",
      "16.3  By submitting User Content, you grant Medily a licence to use, store, process, analyse, reproduce, display and adapt that content for the purpose of providing the Services, generating feedback, quality assurance, support, safety, legal compliance and service improvement.",
      "16.4  Where User Content contains personal data, it will be handled in accordance with the Privacy Policy.",
      "16.5  You must not submit content that is unlawful, abusive, discriminatory, defamatory, confidential to a third party, infringing, unsafe or inappropriate.",
      "16.6  You must not record Live Sessions, interviewers, tutors, staff or other students without Medily’s prior written consent.",
    ],
  },
  {
    id: "data-protection-and-privacy",
    heading: "17. Data Protection and Privacy",
    body: [
      "17.1  Medily processes personal data in accordance with its Privacy Policy and applicable UK data protection law.",
      "17.2  Personal data may include account information, purchase records, communications, learning activity, AI responses, transcripts, recordings, technical information and support messages.",
      "17.3  AI responses, recordings and transcripts are retained only as described in the Privacy Policy for service delivery, quality assurance, support and improvement.",
      "17.3  Users should read the Privacy Policy before using the Services.",
      "17.4  Where a student is under 18, Medily may take additional steps relating to consent, safety, parental involvement and age-appropriate handling of information.",
    ],
  },
  {
    id: "consumer-cancellation-rights",
    heading: "18. Consumer Cancellation Rights",
    body: [
      "18.1  If you are a UK consumer purchasing online, you may have statutory cancellation rights under consumer law.",
      "18.2  Different cancellation rules apply to services, digital content, live sessions, subscriptions and mixed packages.",
      "18.3  Where Digital Content is supplied immediately with your consent and acknowledgement, your right to cancel may be lost once supply begins, to the extent permitted by law.",
      "18.4  Where services begin during the cancellation period at your request, Medily may be entitled to charge for services supplied before cancellation, subject to applicable law.",
      "18.5  The model cancellation form in Schedule 1 may be used to cancel where a statutory right to cancel applies, although use of the form is not mandatory.",
    ],
  },
  {
    id: "refunds-and-cancellations",
    heading: "19. Refunds and Cancellations",
    body: [
      "19.1  Refunds and cancellations are governed by the Refund and Cancellation Policy and any product-specific terms shown at checkout.",
      "19.2  Nothing in these Terms affects your statutory consumer rights.",
      "19.3  Medily may refuse refunds where access has been used, Digital Content has been supplied, a session has been missed without sufficient notice, or the request falls outside the applicable policy, unless the law requires otherwise.",
      "19.4  Medily may issue refunds, credits or extensions at its discretion where appropriate, but doing so once does not create an obligation to do so again.",
    ],
  },
  {
    id: "availability-and-third-party-providers",
    heading: "20. Availability and Third-Party Providers",
    body: [
      "20.1  Medily aims to provide reliable access but does not guarantee uninterrupted, error-free or continuous availability.",
      "20.2  Access may be interrupted by maintenance, technical issues, cyber incidents, payment provider downtime, hosting issues, AI provider issues, internet outages or events outside Medily’s control.",
      "20.3  Medily may rely on third-party providers for payments, hosting, analytics, AI functionality, email, video calls, calendars, learning management, support or communications.",
      "20.4  Medily is not responsible for third-party websites, policies, outages or acts, except where required by law.",
      "20.5  Medily is not liable for delays caused by events beyond its reasonable control, including cyber incidents, internet outages, illness, industrial action, severe weather or government action.",
    ],
  },
  {
    id: "safeguarding-and-welfare",
    heading: "21. Safeguarding and Welfare",
    body: [
      "21.1  Medily may provide Services to students under 18 and is committed to appropriate safeguarding and welfare standards.",
      "21.2  Users, staff and contractors must behave respectfully and appropriately at all times.",
      "21.3  Where Medily identifies a safeguarding concern, welfare risk, abuse, serious misconduct or risk of harm, Medily may take appropriate action, including restricting access, contacting a parent or guardian, escalating internally or contacting relevant authorities where necessary.",
      "21.4  Safeguarding matters are handled in accordance with the Medily Safeguarding Policy.",
    ],
  },
  {
    id: "complaints",
    heading: "22. Complaints",
    body: [
      "22.1  Complaints should be sent to info@medily.co with enough detail for Medily to investigate.",
      "22.2  Medily will aim to acknowledge and investigate complaints within a reasonable timeframe.",
      "22.3  Medily may request further information where necessary.",
      "22.4  Complaints are handled in accordance with the Medily Complaints Policy.",
    ],
  },
  {
    id: "limitation-of-liability",
    heading: "23. Limitation of Liability",
    body: [
      "23.1  Nothing in these Terms limits or excludes liability for death or personal injury caused by negligence, fraud, fraudulent misrepresentation, breach of statutory rights where liability cannot be excluded, or any other liability that cannot lawfully be excluded.",
      "23.2  Subject to clause 23.1, Medily is not liable for loss of profit, loss of business, loss of opportunity, loss of goodwill, loss of data, admissions outcomes, university decisions, indirect loss or consequential loss.",
      "23.3  Subject to clause 23.1, Medily is not liable for losses caused by your misuse of Services, breach of these Terms, reliance on unofficial information, third-party platform failures or circumstances outside Medily’s reasonable control.",
      "23.4  Subject to clause 23.1, Medily’s total liability arising out of or in connection with the Services will not exceed the amount you paid to Medily for the affected Service in the 12 months before the claim arose, unless the law requires otherwise.",
    ],
  },
  {
    id: "suspension-and-termination",
    heading: "24. Suspension and Termination",
    body: [
      "24.1  Medily may suspend or terminate access where you breach these Terms, payment fails, fraud is suspected, account sharing occurs, content is misused, conduct is abusive, or legal, technical, safeguarding or reputational risk arises.",
      "24.2  Where access is terminated due to your breach, you may not be entitled to a refund.",
      "24.3  You may stop using Medily at any time, subject to subscription cancellation rules and any outstanding payment obligations.",
      "24.4  Clauses relating to intellectual property, prohibited sharing, confidentiality, limitation of liability, governing law and payment obligations survive termination.",
      "24.5  Failure to enforce any provision immediately does not waive Medily's rights.",
      "24.6  These Terms constitute the entire agreement regarding the Services.",
      "24.7  Medily may assign these Terms as part of a business sale or restructuring without affecting your statutory rights.",
    ],
  },
  {
    id: "changes-to-services-and-terms",
    heading: "25. Changes to Services and Terms",
    body: [
      "25.1  Medily may update, modify, improve, replace or discontinue parts of the Services from time to time.",
      "25.2  Where a change materially reduces an active paid Service, Medily will act reasonably and may offer an alternative, extension, partial refund or other remedy where appropriate.",
      "25.3  Medily may update these Terms from time to time. The updated version will be posted on the website with a revised effective date.",
      "25.4  Where changes materially affect active paid subscriptions or important rights, Medily will take reasonable steps to notify affected users.",
    ],
  },
  {
    id: "governing-law-and-jurisdiction",
    heading: "26. Governing Law and Jurisdiction",
    body: [
      "26.1  These Terms are governed by the laws of England and Wales.",
      "26.2  The courts of England and Wales will have jurisdiction over disputes, except where consumer law gives you the right to bring proceedings in another UK jurisdiction.",
      "26.3  If any provision is found unlawful, invalid or unenforceable, the remaining provisions continue in force.",
    ],
  },
  {
    id: "contact-details",
    heading: "27. Contact Details",
    body: [
      "27.1  Medily Ltd can be contacted at: info@medily.co",
      "27.4  Website: www.medily.co",
      "27.5  Formal notices may be served by email unless the law requires another method.",
    ],
  },
];

const TermsAndConditions = () => (
  <LegalPage
    title="Website Terms and Conditions"
    subtitle="These terms govern access to and use of the Medily website, student portal, digital resources, AI interview tools, online courses, mock MMI preparation, live sessions and any other Medily products or services."
    sections={SECTIONS}
  />
);

export default TermsAndConditions;
